import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, CheckSquare, Clock } from 'lucide-react';
import { Link } from 'react-router';
import { PageHeader } from '../components/layout';
import { Card, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export function DashboardPage() {
  const { user } = useAuth();

  const agenda = useQuery({
    queryKey: ['agenda'],
    queryFn: api.cursos.agenda,
  });

  const tarefas = useQuery({
    queryKey: ['tarefas-kanban'],
    queryFn: api.tarefas.kanban,
  });

  const proximas = (agenda.data ?? [])
    .filter((e) => new Date(e.startsAt) >= new Date())
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, 5);

  const pendentes = (tarefas.data?.colunas ?? [])
    .flatMap((c) => c.items)
    .filter((t) => t.status !== 'concluido' && t.dueDate)
    .sort((a, b) => {
      if (a.atrasada !== b.atrasada) return a.atrasada ? -1 : 1;
      return (a.dueDate ?? '').localeCompare(b.dueDate ?? '');
    })
    .slice(0, 5);

  const loading = agenda.isLoading || tarefas.isLoading;

  return (
    <>
      <PageHeader
        title={`Olá, ${user?.name.split(' ')[0] ?? ''}!`}
        description="Próximas turmas e tarefas com prazo."
      />

      {loading ? <Spinner /> : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card
          title="Próximas turmas"
          actions={
            <Link to="/agenda" className="text-brand-600 text-sm font-semibold hover:underline">
              Ver agenda
            </Link>
          }
        >
          {proximas.length === 0 ? (
            <p className="text-ink-500 text-sm">Nenhuma turma agendada.</p>
          ) : (
            <ul className="divide-ink-200 divide-y">
              {proximas.map((evento) => {
                const inicio = new Date(evento.startsAt);
                const hoje = new Date();
                const diasAte = Math.ceil(
                  (inicio.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24),
                );
                return (
                  <li key={evento.id} className="flex items-start gap-3 py-2.5">
                    <span className="bg-brand-50 text-brand-700 rounded-button flex size-9 shrink-0 items-center justify-center">
                      <CalendarDays className="size-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-800 truncate text-sm font-medium">
                        {evento.courseTitle}
                      </p>
                      <p className="text-ink-500 text-xs">
                        {evento.dataTexto}
                        {evento.room ? ` · ${evento.room}` : null}
                      </p>
                      <p className="text-ink-400 text-xs">
                        {evento.confirmadas}/{evento.seatsTotal} inscritos
                      </p>
                    </div>
                    <span
                      className={`rounded-pill shrink-0 px-2 py-0.5 text-xs font-medium ${
                        diasAte <= 3
                          ? 'bg-price-50 text-price-600'
                          : diasAte <= 7
                            ? 'bg-accent-50 text-accent-700'
                            : 'bg-ink-100 text-ink-500'
                      }`}
                    >
                      {diasAte === 0 ? 'Hoje' : diasAte === 1 ? 'Amanhã' : `em ${diasAte} dias`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Tarefas com prazo"
          actions={
            <Link to="/tarefas" className="text-brand-600 text-sm font-semibold hover:underline">
              Ver todas
            </Link>
          }
        >
          {pendentes.length === 0 ? (
            <p className="text-ink-500 text-sm">Nenhuma tarefa com prazo pendente.</p>
          ) : (
            <ul className="divide-ink-200 divide-y">
              {pendentes.map((tarefa) => {
                const prazo = tarefa.dueDate ? new Date(tarefa.dueDate) : null;
                const prazoStr = prazo
                  ? prazo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                  : null;
                return (
                  <li key={tarefa.id} className="flex items-start gap-3 py-2.5">
                    <span
                      className={`rounded-button flex size-9 shrink-0 items-center justify-center ${
                        tarefa.atrasada
                          ? 'bg-price-50 text-price-600'
                          : 'bg-stock-50 text-stock-600'
                      }`}
                    >
                      {tarefa.atrasada ? (
                        <AlertTriangle className="size-4" aria-hidden />
                      ) : (
                        <CheckSquare className="size-4" aria-hidden />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-800 truncate text-sm font-medium">{tarefa.title}</p>
                      {tarefa.assigneeName ? (
                        <p className="text-ink-500 text-xs">{tarefa.assigneeName}</p>
                      ) : null}
                    </div>
                    {prazoStr ? (
                      <span
                        className={`rounded-pill flex shrink-0 items-center gap-1 px-2 py-0.5 text-xs font-medium ${
                          tarefa.atrasada ? 'bg-price-50 text-price-600' : 'bg-ink-100 text-ink-500'
                        }`}
                      >
                        <Clock className="size-3" aria-hidden />
                        {prazoStr}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
